import { ChangeDetectionStrategy, Component, inject, isDevMode, OnDestroy, OnInit, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Subscription } from "rxjs";
import { FeatureFlagService } from "../services/feature-flag.service";

interface FlagRow {
    name: string;
    value: boolean;
    overridden: boolean;
}

@Component({
    selector: "flag-dev-overlay",
    standalone: true,
    imports: [MatButtonModule, MatIconModule, MatSlideToggleModule, MatTooltipModule],
    templateUrl: "./flag-dev-overlay.component.html",
    styleUrl: "./flag-dev-overlay.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlagDevOverlayComponent implements OnInit, OnDestroy {
    protected readonly isDev = isDevMode();
    protected readonly featureFlagService = inject(FeatureFlagService);

    protected readonly isOpen = signal(false);
    protected readonly flags = signal<FlagRow[]>([]);

    private sub: Subscription;

    ngOnInit() {
        if (!this.isDev) return;
        this.refreshFlags();
        this.sub = this.featureFlagService.flagsUpdated$.subscribe(() => this.refreshFlags());
    }

    private refreshFlags() {
        const all = this.featureFlagService.getAllFlags();
        this.flags.set(
            Object.entries(all)
                .map(([name, value]) => ({
                    name,
                    value,
                    overridden: this.featureFlagService.isOverridden(name),
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    protected toggle(flag: FlagRow) {
        this.featureFlagService.override(flag.name, !flag.value);
    }

    protected resetAll() {
        this.featureFlagService.clearOverrides();
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
    }
}
